import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Empty, Input, Modal, Popconfirm, Popover, Segmented, Switch, Tag, Tree, TreeSelect, message } from 'antd';
import Tooltip from '../common/Tooltip';
import { CaretUpOutlined, CaretDownOutlined, FilterFilled, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import shallow from 'zustand/shallow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useStore } from '../../store/store';
import Loader from '../../Loader';
import { canAdministrate, canAdministrateGeneralRules } from '../../utils/browser';
import { useQueryDocumentTypes } from '../../hooks';
import { PRIMARY_DARK } from '../../constants/constants';
import {
  COMPACT_FORMAT_ICON_SIZE,
  documentFormatIconStyle,
  documentFormatLabel,
} from '../../utils/documentFormat';
import {
  createFolder,
  updateFolder,
  deleteFolder,
  moveTemplate,
} from '../../requests/folders';
import './DocumentsFolderView.css';

// Icônes FontAwesome dispo dans les deux jeux (light = éditeur standalone,
// duotone = bibliothèque costing) : le macro `icon()` n'accepte que des
// littéraux pour `style`, donc les deux variantes sont résolues ici une fois
// pour toutes, et choisies au rendu via la prop iconStyle (cf. faIcon ci-dessous).
export const ICONS = {
  'copy': { light: icon({ name: 'copy', style: 'light' }), duotone: icon({ name: 'copy', style: 'duotone' }) },
  'eye': { light: icon({ name: 'eye', style: 'light' }), duotone: icon({ name: 'eye', style: 'duotone' }) },
  'file': { light: icon({ name: 'file', style: 'light' }), duotone: icon({ name: 'file', style: 'duotone' }) },
  'file-lines': { light: icon({ name: 'file-lines', style: 'light' }), duotone: icon({ name: 'file-lines', style: 'duotone' }) },
  'file-plus': { light: icon({ name: 'file-plus', style: 'light' }), duotone: icon({ name: 'file-plus', style: 'duotone' }) },
  'folder': { light: icon({ name: 'folder', style: 'light' }), duotone: icon({ name: 'folder', style: 'duotone' }) },
  'folder-arrow-down': { light: icon({ name: 'folder-arrow-down', style: 'light' }), duotone: icon({ name: 'folder-arrow-down', style: 'duotone' }) },
  'folder-plus': { light: icon({ name: 'folder-plus', style: 'light' }), duotone: icon({ name: 'folder-plus', style: 'duotone' }) },
  'globe': { light: icon({ name: 'globe', style: 'light' }), duotone: icon({ name: 'globe', style: 'duotone' }) },
  'gear': { light: icon({ name: 'gear', style: 'light' }), duotone: icon({ name: 'gear', style: 'duotone' }) },
  'pen': { light: icon({ name: 'pen', style: 'light' }), duotone: icon({ name: 'pen', style: 'duotone' }) },
  'trash': { light: icon({ name: 'trash', style: 'light' }), duotone: icon({ name: 'trash', style: 'duotone' }) },
};

// Vue « Dossiers » de la modale Documents : arbre partagé unique stylé en tableau
// (colonnes Nom / Format / Type / Métier / Tags / Actions comme l'ancienne liste).
// La colonne Nom = l'arbre (dossiers dépliables + documents orphelins) ; les autres
// colonnes ne sont remplies que pour les documents. Le Tree antd reste virtualisé
// (prop `height`) pour tenir 700+ documents sans régression perf.
// Ranger un document = bouton « Déplacer vers… » (par doc ou en lot via les coches)
// ou glisser-déposer. Clic sur une ligne document = appliquer (onApply). Les actions
// rename/duplicate/delete réutilisent les handlers de TemplateModal (props).
// Le contrôle d'accès (qui peut créer/déplacer) est hors périmètre pour l'instant.
const selector = (state) => ({
  folders: state.folders,
  templates: state.templates,
  sections: state.sections,
  getTemplates: state.getTemplates,
  templatesLoaded: state.templatesLoaded,
});

// Valeur hex (pas le nom preset "geekblue") : un Tag antd ne rend en plein
// (fond coloré, texte blanc) que si `color` n'est pas un nom de preset —
// avec le nom, il rend en clair/contour, ce qu'on veut pour la variante
// "Section récursive". Même teinte que le preset "geekblue" (@ant-design/colors).
const GEEKBLUE_SOLID = '#2F54EB';

// Étiquette d'une section (récursive ou non) ; rien pour un modèle. Reprise
// par l'accès rapide du panel Documents (DocumentsPanel).
export const SectionTag = ({ item, style }) => {
  if (item.pattern !== 'section') return null;
  return item.isRecursive ? (
    <Tag color='geekblue' style={style}>Section récursive</Tag>
  ) : (
    <Tag color={GEEKBLUE_SOLID} style={style}>Section</Tag>
  );
};

// Seuls les administrateurs reçoivent ces modèles (filtrés côté Rails) : le
// badge leur rappelle que les autres utilisateurs ne les voient pas.
const AdminOnlyTag = ({ item }) => {
  const { t } = useTranslation();
  if (!item.admin_only) return null;
  return <Tag color='gold'>{t('admin_only_template')}</Tag>;
};

// Valeur sentinelle du TreeSelect pour « remettre à la racine » (folder_id null).
const ROOT_VALUE = '__root__';
// Grille partagée en-tête / lignes : Nom(1fr) | Format | Type | Métier | Tags | Actions.
// Format : 110px — son en-tête porte le libellé, l'icône de tri ET l'entonnoir de
// filtre, alors que la ligne n'affiche qu'une icône de fichier centrée. À 90px le
// libellé se coupait ("For…"). Les 20px viennent de Tags, la colonne la plus
// large, dont le contenu est de toute façon tronqué avec une infobulle.
const GRID_COLUMNS = 'minmax(140px, 1fr) 110px 92px 132px 130px 108px';
// Mode sélecteur (création de proposal) : on choisit un modèle, rien à gérer,
// donc pas de colonne Actions.
const GRID_COLUMNS_NO_ACTIONS = 'minmax(140px, 1fr) 110px 92px 132px 130px';

export const normalizeText = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

export const compareTextValues = (a, b) => normalizeText(a).localeCompare(normalizeText(b), 'fr');

// Référence stable : passer `[]` en littéral relancerait les mémos à chaque rendu.
const NO_FILTER = [];

// Pré-remplissage de « Nouveau document » selon l'onglet affiché : section
// cochée depuis Sections, section + récursive depuis Sections récursives.
// Depuis Tous, `isSection` reste indéfini : rien n'est imposé. Partagé par
// l'éditeur (TemplateModal) et la bibliothèque du costing (libraryApp).
export const newTemplateDefaults = (docFilter) => ({
  isSection: docFilter === 'templates'
    ? false
    : docFilter === 'sections' || docFilter === 'recursive' ? true : undefined,
  isRecursive: docFilter === 'recursive',
});

// Valeurs d'un document pour les filtres de colonne. Les tags en donnent
// plusieurs : un document coché sur l'un d'eux passe le filtre.
export const documentFilterValues = (it) => ({
  format: [it.landscape ? 'landscape' : 'portrait'],
  type: [it.templatable].filter(Boolean),
  work: [it.work].filter(Boolean),
  tags: (it.document_type_ids ?? []).map(String),
});

/**
 * Choix proposés par colonne : les valeurs réellement présentes dans les
 * documents affichés (comme la prop `filters` d'un Table antd, mais dérivée des
 * données plutôt que figée), triées par libellé.
 */
export const buildColumnFilterOptions = ({ items = [], documentTypes, translate = (v) => v }) => {
  const byField = { format: [], type: [], work: [], tags: [] };
  const push = (field, value, label) => {
    if (!value) return;
    if (!byField[field].some((o) => o.value === value)) byField[field].push({ value, label });
  };
  items.forEach((it) => {
    push('format', it.landscape ? 'landscape' : 'portrait', it.landscape ? 'Paysage' : 'Portrait');
    push('type', it.templatable, translate(it.templatable));
    push('work', it.work, translate(it.work));
    (it.document_type_ids ?? []).forEach((id) => {
      const documentType = documentTypes?.find((dT) => dT.id === id);
      if (documentType) push('tags', String(id), documentType.name);
    });
  });
  Object.values(byField).forEach((list) => list.sort((a, b) => compareTextValues(a.label, b.label)));
  return byField;
};

/**
 * Un document passe-t-il les filtres de colonne ? Plusieurs colonnes filtrées se
 * cumulent (ET), les valeurs cochées d'une même colonne s'additionnent (OU) :
 * la règle d'un Table antd.
 */
export const matchesColumnFilters = (it, columnFilters = {}, fields = null) => {
  const activeFields = fields
    ?? Object.keys(columnFilters).filter((field) => (columnFilters[field] ?? []).length > 0);
  if (activeFields.length === 0) return true;
  const values = documentFilterValues(it);
  return activeFields.every((field) =>
    (columnFilters[field] ?? []).some((v) => (values[field] ?? []).includes(v)));
};

// Filtre de colonne, même geste que celui d'un Table antd (recherche, cases à
// cocher, Réinitialiser / OK) : rejoué à la main comme le tri, parce que les
// lignes sont un Tree stylé en tableau et non un Table — `filterDropdown` et les
// classes internes d'antd n'existent pas ici.
// La sélection est retenue à part tant que le panneau est ouvert : elle ne
// s'applique qu'au clic sur OK, comme chez antd.
export const ColumnFilterDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [staged, setStaged] = useState(value);

  const needle = normalizeText(search);
  const visibleOptions = needle
    ? options.filter((o) => normalizeText(o.label).includes(needle))
    : options;

  const toggle = (optionValue) =>
    setStaged((prev) =>
      prev.includes(optionValue)
        ? prev.filter((v) => v !== optionValue)
        : [...prev, optionValue],
    );

  const content = (
    <div style={{ width: 232 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8, borderBottom: '1px solid rgba(11, 34, 57, 0.08)' }}>
        <Input
          allowClear
          size='small'
          prefix={<SearchOutlined style={{ opacity: 0.45 }} />}
          placeholder='Chercher dans les filtres'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto', padding: '6px 10px' }}>
        {visibleOptions.length === 0 ? (
          <div style={{ padding: '6px 0', color: 'rgba(11, 34, 57, 0.45)' }}>Aucun filtre</div>
        ) : (
          visibleOptions.map((o) => (
            <div key={o.value} style={{ padding: '3px 0' }}>
              <Checkbox checked={staged.includes(o.value)} onChange={() => toggle(o.value)}>
                {o.label}
              </Checkbox>
            </div>
          ))
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          borderTop: '1px solid rgba(11, 34, 57, 0.08)',
        }}
      >
        <Button
          type='link'
          size='small'
          disabled={staged.length === 0}
          onClick={() => {
            setStaged([]);
            onChange(NO_FILTER);
            setOpen(false);
          }}
        >
          Réinitialiser
        </Button>
        <Button
          type='primary'
          size='small'
          onClick={() => {
            onChange(staged);
            setOpen(false);
          }}
        >
          OK
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // À l'ouverture on repart de la sélection appliquée, recherche vidée.
        if (next) {
          setStaged(value);
          setSearch('');
        }
        setOpen(next);
      }}
      trigger='click'
      placement='bottomRight'
      arrow={false}
      content={content}
      overlayInnerStyle={{ padding: 0 }}
    >
      {/* stopPropagation : l'en-tête de colonne porte le clic de tri. */}
      <span
        className={`doc-filter-icon${value.length > 0 ? ' active' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <FilterFilled />
      </span>
    </Popover>
  );
};

// Mêmes icônes qu'antd Table (CaretUp/DownOutlined), mais stylées à la main
// (.doc-sort-icon) : ce Tree n'est pas un vrai Table, les classes internes
// ant-table-column-sorter-* d'antd ne s'appliquent pas en dehors d'un Table
// réellement monté (antd 5 = CSS-in-JS, bucket jamais injecté ici).
export const SortIcon = ({ order }) => (
  <span className='doc-sort-icon'>
    <CaretUpOutlined className={order === 'ascend' ? 'active' : ''} />
    <CaretDownOutlined className={order === 'descend' ? 'active' : ''} />
  </span>
);

// Tri à 3 états par colonne (ascendant → descendant → aucun), une seule
// colonne active à la fois, comme le sorter unique d'antd Table.
export const nextSortState = (prev, field) => {
  if (prev.field !== field) return { field, order: 'ascend' };
  if (prev.order === 'ascend') return { field, order: 'descend' };
  return { field: null, order: null };
};

const DocumentsFolderView = ({
  onApply,
  onRename,
  onDuplicate,
  onDelete,
  onNewTemplate,
  // Consultation des éléments cachés (superadmin) : reprise du switch de
  // l'ancienne vue Liste. `onShowHiddenChange` absent = pas le droit, donc pas
  // de switch. En mode caché, les documents viennent des listes passées en props
  // (le store garde les visibles) et la seule action possible est la restauration.
  showHidden = false,
  onShowHiddenChange,
  hiddenTemplates = [],
  hiddenSections = [],
  onRestore,
  // Jeu d'icônes FontAwesome ('light' par défaut, comme l'éditeur standalone).
  // La vue bibliothèque du costing passe 'duotone' pour s'aligner sur les
  // icônes duotone utilisées côté costing (cf. libraryApp.jsx).
  iconStyle = 'light',
  // Mode sélecteur pur (choix du modèle à la création d'une proposal) : ne
  // montre que les modèles (pas les sections), et masque tout le bandeau
  // (onglets, nouveau dossier, règles générales) ainsi que les cases à cocher
  // et le drag&drop, dont les actions (déplacer en lot) n'ont plus de bouton
  // pour s'exécuter.
  templatesOnly = false,
}) => {
  const faIcon = (name) => ICONS[name][iconStyle];
  const { t } = useTranslation();
  // shallow : le sélecteur renvoie un objet ; sans lui (zustand v3 compare par
  // référence) le composant re-render à CHAQUE tick du store.
  const {
    folders,
    templates: visibleTemplates,
    sections: visibleSections,
    getTemplates,
    templatesLoaded,
  } = useStore(selector, shallow);
  const templates = showHidden ? hiddenTemplates : visibleTemplates;
  const sections = showHidden ? hiddenSections : visibleSections;
  const { data: documentTypes } = useQueryDocumentTypes();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  // Filtre de type de document (comme les onglets de l'ancienne liste).
  const [docFilter, setDocFilter] = useState('all'); // all | templates | sections | recursive
  // Éditeur de dossier (création / renommage) : un seul petit modal réutilisé.
  const [editor, setEditor] = useState(null); // { mode, parentId?, folder?, name }
  // Sélecteur de dossier de destination : { itemIds, target }.
  const [mover, setMover] = useState(null);
  // Recherche par nom + tri de colonne (comme ProposalList/TemplateModal),
  // rejoués ici à la main car les lignes sont un Tree stylé, pas un Table antd.
  const [nameSearch, setNameSearch] = useState('');
  const [sortState, setSortState] = useState({ field: null, order: null }); // order: 'ascend' | 'descend'
  // Filtres de colonne : { [champ]: valeurs cochées }. Un champ absent ou à
  // liste vide ne filtre rien.
  const [columnFilters, setColumnFilters] = useState({});
  const folderNameInputRef = useRef(null);
  // Dossiers et documents de la bibliothèque ne se modifient qu'avec
  // `permissions.web_admin` (cf. FolderPolicy / TemplatePolicy) : sans lui,
  // l'API répond 403, donc lecture et application seulement.
  const canManage = canAdministrate();
  const gridColumns = templatesOnly ? GRID_COLUMNS_NO_ACTIONS : GRID_COLUMNS;

  // Hauteur de l'arbre = espace réellement dispo dans son conteneur flex (mesuré
  // via clientHeight), pour remplir toute la place et virtualiser (la prop
  // `height` numérique active rc-virtual-list). Marche que le composant soit dans
  // la modale Documents (parent = .ant-modal-body à hauteur définie) ou en plein
  // écran dans la vue bibliothèque du costing (parent = 100vh). Le conteneur
  // mesuré est en flex:1 minHeight:0 → son clientHeight = l'espace restant.
  // Ref callback et non un effet au montage : l'arbre n'est rendu qu'une fois les
  // documents chargés (avant, c'est l'état vide). Un effet à dépendances vides ne
  // voyait donc qu'un ref à null, n'observait rien, et la hauteur restait à sa
  // valeur initiale — l'arbre gardait 420px au milieu d'une page bien plus haute
  // (visible dans la vue bibliothèque du costing, où les documents arrivent
  // toujours après le montage). Le ref callback est rappelé quand le conteneur
  // apparaît ou disparaît, donc la mesure suit.
  // Pas de listener sur window : redimensionner la fenêtre change la taille du
  // conteneur, ce que l'observer voit déjà.
  const [treeHeight, setTreeHeight] = useState(420);
  const treeResizeObserver = useRef(null);
  const setTreeWrapNode = useCallback((el) => {
    treeResizeObserver.current?.disconnect();
    treeResizeObserver.current = null;
    if (!el) return;
    const compute = () => setTreeHeight(Math.max(220, Math.floor(el.clientHeight)));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    treeResizeObserver.current = ro;
  }, []);

  // Documents affichés selon le filtre : modèles, sections simples, sections
  // récursives (flag isRecursive, cf. TemplateModal), ou tout.
  // templatesOnly ignore le filtre (bandeau masqué, pas de contrôle dessus) et
  // exclut toujours les sections.
  const items = useMemo(() => {
    if (templatesOnly) return templates;
    switch (docFilter) {
      case 'templates':
        return templates;
      case 'sections':
        return sections.filter((s) => !s.isRecursive);
      case 'recursive':
        return sections.filter((s) => s.isRecursive);
      default:
        return [...templates, ...sections];
    }
  }, [templates, sections, docFilter, templatesOnly]);

  const foldersByParent = useMemo(() => {
    const map = new Map();
    folders.forEach((f) => {
      const key = f.parent_id ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    });
    return map;
  }, [folders]);

  const getTagNames = (record) =>
    documentTypes
      ?.filter((dT) => record.document_type_ids?.includes(dT.id))
      .map((dT) => dT.name)
      .join(', ') || '';

  // Valeur d'un document pour une colonne triable (le format se réduit au
  // booléen paysage/portrait, comme le sorter historique de TemplateModal).
  const fieldValue = (it, field) => {
    switch (field) {
      case 'name': return t(it.name);
      case 'format': return it.landscape ? 1 : 0;
      case 'type': return t(it.templatable || '');
      case 'work': return t(it.work || '');
      case 'tags': return getTagNames(it);
      default: return '';
    }
  };

  const filterOptions = useMemo(
    () => buildColumnFilterOptions({ items, documentTypes, translate: t }),
    [items, documentTypes, t],
  );

  const activeFilterFields = useMemo(
    () => Object.keys(columnFilters).filter((field) => (columnFilters[field] ?? []).length > 0),
    [columnFilters],
  );

  // Le nom brut est comparé en plus du nom traduit : les documents portant une
  // clé de traduction ne matchaient sinon que par leur libellé affiché, alors que
  // l'ancienne liste acceptait les deux.
  const filteredItems = useMemo(() => {
    const needle = nameSearch.trim() ? normalizeText(nameSearch) : null;
    if (!needle && activeFilterFields.length === 0) return items;
    return items.filter((it) => {
      if (needle
        && !normalizeText(t(it.name)).includes(needle)
        && !normalizeText(it.name).includes(needle)) return false;
      return matchesColumnFilters(it, columnFilters, activeFilterFields);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, nameSearch, activeFilterFields, columnFilters, t]);

  const sortedItems = useMemo(() => {
    if (!sortState.field || !sortState.order) return filteredItems;
    const dir = sortState.order === 'ascend' ? 1 : -1;
    return [...filteredItems].sort((a, b) => {
      const valueA = fieldValue(a, sortState.field);
      const valueB = fieldValue(b, sortState.field);
      if (typeof valueA === 'number' || typeof valueB === 'number') return (valueA - valueB) * dir;
      return compareTextValues(valueA, valueB) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, sortState]);

  const itemsByFolder = useMemo(() => {
    const map = new Map();
    sortedItems.forEach((it) => {
      const key = it.folder_id ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return map;
  }, [sortedItems]);

  // Recherche active : un dossier ne doit apparaître que s'il contient (ou si
  // l'un de ses sous-dossiers contient) un document qui matche, sinon la vue
  // affiche des dossiers vides et cache les résultats en les laissant repliés.
  const qualifyingFolderIds = useMemo(() => {
    if (!nameSearch.trim() && activeFilterFields.length === 0) return null;
    const qualifying = new Set();
    const check = (folderId) => {
      const direct = (itemsByFolder.get(folderId) || []).length > 0;
      const childQualifies = (foldersByParent.get(folderId) || []).some((f) => check(f.id));
      if (direct || childQualifies) qualifying.add(folderId);
      return direct || childQualifies;
    };
    (foldersByParent.get(null) || []).forEach((f) => check(f.id));
    return qualifying;
  }, [nameSearch, activeFilterFields, itemsByFolder, foldersByParent]);

  const refresh = () => getTemplates();

  // Clés des dossiers ancestraux (racine → parent direct inclus), pour déplier
  // la branche menant à un dossier donné.
  const ancestorKeys = (folderId) => {
    const byId = new Map(folders.map((f) => [f.id, f]));
    const keys = [];
    let currentId = folderId;
    while (currentId != null) {
      const folder = byId.get(currentId);
      if (!folder) break;
      keys.push(`f-${folder.id}`);
      currentId = folder.parent_id ?? null;
    }
    return keys;
  };

  const submitEditor = async () => {
    const name = (editor.name || '').trim();
    if (!name) return;
    if (editor.mode === 'create') {
      await createFolder({ name, parent_id: editor.parentId ?? null });
      // Un sous-dossier créé dans une branche repliée resterait invisible : on
      // déplie ses parents pour que l'utilisateur voie tout de suite le résultat.
      if (editor.parentId != null) {
        setExpandedKeys((prev) => [...new Set([...prev, ...ancestorKeys(editor.parentId)])]);
      }
    } else {
      await updateFolder(editor.folder.id, { name });
    }
    setEditor(null);
    refresh();
  };

  const handleDeleteFolder = async (folder) => {
    await deleteFolder(folder.id);
    refresh();
  };

  const checkedItemIds = useMemo(
    () => checkedKeys.filter((k) => typeof k === 'string' && k.startsWith('t-')).map((k) => Number(k.slice(2))),
    [checkedKeys],
  );

  const confirmMove = async () => {
    if (!mover) return;
    const dest = mover.target === ROOT_VALUE ? null : mover.target;
    await Promise.all(mover.itemIds.map((id) => moveTemplate(id, dest)));
    setMover(null);
    setCheckedKeys([]);
    refresh();
  };

  const handleBulkDelete = async () => {
    await Promise.all(checkedItemIds.map((id) => onDelete(id)));
    setCheckedKeys([]);
  };

  // Déplacement DnD (bonus). Cible = dossier survolé, ou dossier conteneur du
  // noeud voisin (drop entre deux noeuds / à la racine).
  const onDrop = async (info) => {
    const drag = info.dragNode;
    const dropNode = info.node;
    const targetFolderId =
      !info.dropToGap && dropNode.isFolder
        ? dropNode.folderId
        : dropNode.parentFolderId ?? null;

    if (drag.itemId != null) {
      await moveTemplate(drag.itemId, targetFolderId);
      refresh();
      return;
    }
    if (drag.folderId != null && targetFolderId !== drag.folderId) {
      const res = await updateFolder(drag.folderId, { parent_id: targetFolderId });
      if (res) refresh();
      else message.error('Déplacement de dossier impossible.');
    }
  };

  // --- Rendu des lignes (colonnes alignées via .doc-tree-row) ---
  const formatCell = (it) => (
    <span className='doc-tree-cell' style={{ textAlign: 'center' }}>
      <Tooltip title={documentFormatLabel(it.landscape)} mouseLeaveDelay={0}>
        <FontAwesomeIcon
          icon={faIcon('file')}
          style={documentFormatIconStyle(it.landscape, COMPACT_FORMAT_ICON_SIZE)}
        />
      </Tooltip>
    </span>
  );

  const folderTitle = (folder) => (
    <div className='doc-tree-row' style={{ gridTemplateColumns: gridColumns }}>
      <span className='doc-tree-cell' style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <FontAwesomeIcon icon={faIcon('folder')} style={{ color: PRIMARY_DARK, opacity: 0.7 }} />
        <span style={{ fontWeight: 600, color: PRIMARY_DARK }}>{folder.name}</span>
      </span>
      <span />
      <span />
      <span />
      <span />
      {!templatesOnly && (
      <span className='doc-tree-actions'>
        {canManage && (
        <>
        <Tooltip title='Nouveau sous-dossier' mouseLeaveDelay={0}>
          <FontAwesomeIcon
            icon={faIcon('folder-plus')}
            style={actionIconStyle}
            onClick={(e) => {
              e.stopPropagation();
              setEditor({ mode: 'create', parentId: folder.id, name: '' });
            }}
          />
        </Tooltip>
        <Tooltip title='Renommer le dossier' mouseLeaveDelay={0}>
          <FontAwesomeIcon
            icon={faIcon('pen')}
            style={actionIconStyle}
            onClick={(e) => {
              e.stopPropagation();
              setEditor({ mode: 'rename', folder, name: folder.name });
            }}
          />
        </Tooltip>
        <Tooltip title='Supprimer le dossier' mouseLeaveDelay={0}>
          <Popconfirm
            title='Supprimer ce dossier ? Les documents qu’il contient repartent à la racine.'
            okText={t('yes')}
            cancelText={t('cancel')}
            onConfirm={() => handleDeleteFolder(folder)}
          >
            <FontAwesomeIcon
              icon={faIcon('trash')}
              style={{ ...actionIconStyle, color: '#cf1322' }}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Tooltip>
        </>
        )}
      </span>
      )}
    </div>
  );

  const itemTitle = (it) => {
    const tags = getTagNames(it);
    return (
      <div className='doc-tree-row' style={{ gridTemplateColumns: gridColumns }}>
        <span className='doc-tree-cell' style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faIcon('file-lines')} style={{ opacity: 0.6 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(it.name)}</span>
          <SectionTag item={it} />
          <AdminOnlyTag item={it} />
        </span>
        {formatCell(it)}
        <span className='doc-tree-cell'>{it.templatable ? t(it.templatable) : '—'}</span>
        <span className='doc-tree-cell'>{it.work ? t(it.work) : '—'}</span>
        <Tooltip title={tags} mouseLeaveDelay={0}>
          <span className='doc-tree-cell'>{tags || '—'}</span>
        </Tooltip>
        {!templatesOnly && (
        <span className='doc-tree-actions' onClick={(e) => e.stopPropagation()}>
          {showHidden ? (
            onRestore && (
              <Tooltip title="Afficher l'élément" mouseLeaveDelay={0}>
                <FontAwesomeIcon
                  icon={faIcon('eye')}
                  style={actionIconStyle}
                  onClick={() => onRestore(it.id)}
                />
              </Tooltip>
            )
          ) : (
            <>
          {canManage && (
          <Tooltip title='Déplacer vers un dossier' mouseLeaveDelay={0}>
            <FontAwesomeIcon
              icon={faIcon('folder-arrow-down')}
              style={actionIconStyle}
              onClick={() => setMover({ itemIds: [it.id], target: it.folder_id ?? ROOT_VALUE })}
            />
          </Tooltip>
          )}
          {canManage && onRename && (
            <Tooltip title='Renommer' mouseLeaveDelay={0}>
              <FontAwesomeIcon
                icon={faIcon('pen')}
                style={actionIconStyle}
                onClick={() => onRename(it)}
              />
            </Tooltip>
          )}
          {canManage && onDuplicate && (
            <Tooltip title='Dupliquer' mouseLeaveDelay={0}>
              <FontAwesomeIcon
                icon={faIcon('copy')}
                style={actionIconStyle}
                onClick={() => onDuplicate(it)}
              />
            </Tooltip>
          )}
          {canManage && onDelete && (
            <Tooltip title='Supprimer' mouseLeaveDelay={0}>
              <Popconfirm
                title='Supprimer ce document ?'
                okText={t('yes')}
                cancelText={t('cancel')}
                onConfirm={() => onDelete(it.id)}
              >
                <FontAwesomeIcon
                  icon={faIcon('trash')}
                  style={{ ...actionIconStyle, color: '#cf1322' }}
                />
              </Popconfirm>
            </Tooltip>
          )}
            </>
          )}
        </span>
        )}
      </div>
    );
  };

  // treeData = données plates mémoïsées (titres rendus par titleRender → seulement
  // les nœuds visibles, cf. virtualisation). Dossiers non cochables ; seuls les
  // documents le sont (pour le lot). Les documents non classés suivent les
  // dossiers racine.
  const treeData = useMemo(() => {
    const visibleFolders = (list) =>
      qualifyingFolderIds ? list.filter((f) => qualifyingFolderIds.has(f.id)) : list;
    const buildItemNode = (it) => ({
      key: `t-${it.id}`,
      // title: '' — sans ça, rc-tree pose son propre defaultTitle ('---')
      // comme attribut HTML title sur .ant-tree-node-content-wrapper, d'où
      // une infobulle native "---" au survol de chaque ligne. Le contenu
      // visuel vient de titleRender, ce champ ne sert qu'à éviter ce défaut.
      title: '',
      isLeaf: true,
      isFolder: false,
      itemId: it.id,
      parentFolderId: it.folder_id ?? null,
      item: it,
    });
    const buildFolderNode = (folder) => ({
      key: `f-${folder.id}`,
      title: '',
      isFolder: true,
      checkable: false,
      folderId: folder.id,
      parentFolderId: folder.parent_id ?? null,
      folder,
      children: [
        ...visibleFolders(foldersByParent.get(folder.id) || []).map(buildFolderNode),
        ...(itemsByFolder.get(folder.id) || []).map(buildItemNode),
      ],
    });
    const rootFolders = visibleFolders(foldersByParent.get(null) || []).map(buildFolderNode);
    const rootItems = (itemsByFolder.get(null) || []).map(buildItemNode);
    return [...rootFolders, ...rootItems];
  }, [foldersByParent, itemsByFolder, qualifyingFolderIds]);

  // Pendant une recherche, les dossiers pertinents sont dépliés d'office (sinon
  // un résultat resterait caché dans un dossier fermé) ; l'état manuel reprend
  // dès que la recherche est vidée.
  const effectiveExpandedKeys = useMemo(() => {
    if (!qualifyingFolderIds) return expandedKeys;
    return [...qualifyingFolderIds].map((id) => `f-${id}`);
  }, [qualifyingFolderIds, expandedKeys]);

  const folderSelectData = useMemo(() => {
    const build = (parentId) =>
      (foldersByParent.get(parentId) || []).map((f) => ({
        title: f.name,
        value: f.id,
        children: build(f.id),
      }));
    return [{ title: '⤴ Racine (non classé)', value: ROOT_VALUE }, ...build(null)];
  }, [foldersByParent]);

  const renderTitle = (node) => (node.isFolder ? folderTitle(node.folder) : itemTitle(node.item));

  const onSelect = (_keys, { node }) => {
    if (node.itemId != null) onApply(node.itemId, node.item);
  };

  const toggleSort = (field) => setSortState((prev) => nextSortState(prev, field));

  const sortIcon = (field) => <SortIcon order={sortState.field === field ? sortState.order : null} />;

  // Colonnes catégorielles seulement : le nom se cherche par le champ au-dessus,
  // et « Actions » ne porte pas de valeur.
  const filterIcon = (field) => (
    <ColumnFilterDropdown
      options={filterOptions[field]}
      value={columnFilters[field] ?? NO_FILTER}
      onChange={(next) => setColumnFilters((prev) => ({ ...prev, [field]: next }))}
    />
  );

  return (
    <div style={{ textAlign: 'left', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!templatesOnly && (
      // flexWrap : avec des documents cochés, les boutons groupés poussaient
      // « Règles générales » hors du cadre ; ils passent maintenant à la ligne.
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Segmented
          value={docFilter}
          onChange={(v) => {
            setDocFilter(v);
            setCheckedKeys([]);
            // Les valeurs disponibles changent d'un onglet à l'autre : garder une
            // sélection venue du précédent donnerait une liste vide sans raison
            // visible.
            setColumnFilters({});
          }}
          options={[
            { label: 'Tous', value: 'all' },
            { label: 'Modèles', value: 'templates' },
            { label: 'Sections', value: 'sections' },
            { label: 'Sections récursives', value: 'recursive' },
          ]}
        />
        {onShowHiddenChange && (
          <Tooltip
            title={showHidden ? 'Afficher les éléments cachés' : 'Afficher les éléments visibles'}
            mouseLeaveDelay={0}
          >
            <Switch
              checked={showHidden}
              onChange={(checked) => {
                onShowHiddenChange(checked);
                setCheckedKeys([]);
              }}
              checkedChildren='Cachés'
              unCheckedChildren='Visibles'
            />
          </Tooltip>
        )}
        {!showHidden && checkedItemIds.length > 0 && (
          <>
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faIcon('folder-arrow-down')} style={{ marginRight: 8 }} />}
              onClick={() => setMover({ itemIds: checkedItemIds, target: ROOT_VALUE })}
            >
              {`Déplacer ${checkedItemIds.length} vers…`}
            </Button>
            <Button type='text' onClick={() => setCheckedKeys([])}>
              Tout décocher
            </Button>
            {onDelete && (
              <Popconfirm
                title={`Supprimer ${checkedItemIds.length} document(s) ?`}
                okText={t('yes')}
                cancelText={t('cancel')}
                onConfirm={handleBulkDelete}
              >
                <Button
                  type='primary'
                  danger
                  icon={<FontAwesomeIcon icon={faIcon('trash')} style={{ marginRight: 8 }} />}
                >
                  Tout supprimer
                </Button>
              </Popconfirm>
            )}
          </>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {canManage && onNewTemplate && (
            <Button
              type='primary'
              ghost
              icon={<FontAwesomeIcon icon={faIcon('file-plus')} style={{ marginRight: 8 }} />}
              onClick={() => onNewTemplate(docFilter)}
            >
              Nouveau modèle
            </Button>
          )}
          {canManage && (
          <Button
            icon={<FontAwesomeIcon icon={faIcon('folder-plus')} style={{ marginRight: 8 }} />}
            onClick={() => setEditor({ mode: 'create', parentId: null, name: '' })}
          >
            Nouveau dossier
          </Button>
          )}
          {/* Règles générales : déplacé depuis la barre d'onglets de l'ancienne
              vue Liste, qui est en cours de suppression. */}
          {canAdministrateGeneralRules() && (
            <Tooltip title='Règles générales de la société (administration)' mouseLeaveDelay={0}>
              <Button
                icon={iconStyle === 'duotone'
                  ? <FontAwesomeIcon icon={faIcon('gear')} />
                  : <SettingOutlined />}
                onClick={() => window.open('/admin', '_blank')}
              >
                Règles générales
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      )}

      <div className='doc-tree-header' style={{ gridTemplateColumns: gridColumns }}>
        <span className='doc-tree-header-name-col'>
          <span className='doc-tree-header-sortable' onClick={() => toggleSort('name')}>
            <span className='doc-tree-header-title'>Nom</span>
            {sortIcon('name')}
          </span>
          <Input
            size='small'
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            suffix={<SearchOutlined />}
            className='doc-tree-header-search'
          />
        </span>
        <span className='doc-tree-header-sortable' onClick={() => toggleSort('format')}>
          <span className='doc-tree-header-title'>Format</span>
          {sortIcon('format')}
          {filterIcon('format')}
        </span>
        <span className='doc-tree-header-sortable' onClick={() => toggleSort('type')}>
          <span className='doc-tree-header-title'>Type</span>
          {sortIcon('type')}
          {filterIcon('type')}
        </span>
        <span className='doc-tree-header-sortable' onClick={() => toggleSort('work')}>
          <span className='doc-tree-header-title'>Métier</span>
          {sortIcon('work')}
          {filterIcon('work')}
        </span>
        <span className='doc-tree-header-sortable' onClick={() => toggleSort('tags')}>
          <span className='doc-tree-header-title'>Tags</span>
          {sortIcon('tags')}
          {filterIcon('tags')}
        </span>
        {!templatesOnly && (
          <span>
            <span className='doc-tree-header-title'>Actions</span>
          </span>
        )}
      </div>
      {!showHidden && !templatesLoaded ? (
        // Premier chargement : la liste affichait « Aucun document » le temps
        // de la requête (plusieurs secondes sur une grosse bibliothèque) et
        // passait pour vide ou cassée.
        <Loader
          style={{
            flex: 1,
            minHeight: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          imageStyle={{ width: 240, maxWidth: '50%' }}
        />
      ) : treeData.length === 0 ? (
        <Empty
          description={
            nameSearch.trim() || activeFilterFields.length > 0
              ? 'Aucun document ne correspond à la recherche'
              : 'Aucun document'
          }
        />
      ) : (
        <div ref={setTreeWrapNode} style={{ flex: 1, minHeight: 0 }}>
          <Tree
            className='documents-folder-tree'
            treeData={treeData}
            titleRender={renderTitle}
            blockNode
            checkable={!templatesOnly && canManage}
            draggable={!templatesOnly && canManage}
            selectable
            expandAction='click'
            expandedKeys={effectiveExpandedKeys}
            onExpand={setExpandedKeys}
            checkedKeys={checkedKeys}
            onCheck={(keys) => {
              const arr = Array.isArray(keys) ? keys : keys.checked;
              setCheckedKeys(arr.filter((k) => typeof k === 'string' && k.startsWith('t-')));
            }}
            onSelect={onSelect}
            onDrop={onDrop}
            height={treeHeight}
          />
        </div>
      )}

      <Modal
        open={!!editor}
        title={editor?.mode === 'rename' ? 'Renommer le dossier' : 'Nouveau dossier'}
        okText={editor?.mode === 'rename' ? 'Renommer' : 'Créer'}
        cancelText={t('cancel')}
        onCancel={() => setEditor(null)}
        onOk={submitEditor}
        okButtonProps={{ disabled: !editor?.name?.trim() }}
        // `autoFocus` sur l'Input perd la course contre le focus-trap du Modal,
        // qui reprend le focus une fois son animation d'ouverture terminée :
        // on refocus explicitement à ce moment-là.
        afterOpenChange={(open) => {
          if (open) folderNameInputRef.current?.focus();
        }}
        destroyOnClose
      >
        <Input
          ref={folderNameInputRef}
          placeholder='Nom du dossier'
          value={editor?.name || ''}
          onChange={(e) => setEditor((s) => ({ ...s, name: e.target.value }))}
          onPressEnter={submitEditor}
        />
      </Modal>

      <Modal
        open={!!mover}
        title={mover && mover.itemIds.length > 1 ? `Déplacer ${mover.itemIds.length} documents` : 'Déplacer le document'}
        okText='Déplacer'
        cancelText={t('cancel')}
        onCancel={() => setMover(null)}
        onOk={confirmMove}
        okButtonProps={{ disabled: !mover?.target }}
        destroyOnClose
      >
        <TreeSelect
          style={{ width: '100%' }}
          value={mover?.target}
          onChange={(value) => setMover((s) => ({ ...s, target: value }))}
          treeData={folderSelectData}
          treeDefaultExpandAll
          showSearch
          treeNodeFilterProp='title'
          placeholder='Choisir un dossier de destination'
          dropdownStyle={{ maxHeight: 320, overflow: 'auto' }}
        />
      </Modal>
    </div>
  );
};

const actionIconStyle = { cursor: 'pointer', fontSize: 12, color: 'rgba(0,0,0,0.85)' };

export default DocumentsFolderView;
